import React, { useContext, useState } from 'react';
import { Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Required from './required';
import style from "./../../assets/styles/componentStyles/userProfileStyles/userProfileStyle.module.scss";
import contactStyle from "./../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import popupStyles from "./../../assets/styles/componentStyles/globalPopupStyles.module.scss";
import globalStyles from "./../../assets/styles/global.module.scss";
import { UserContext, UserContextPayload } from '../../context/userContext';
import agent from '../../services/api';
import { useGenericStore } from '../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';
import EmailAddressDisplay from './emailAddressDisplay';

interface UserPasswordFormProps {
    mode: 'set' | 'update';
    renderButton: (text: string, onClick: () => void, className: string | undefined, width: string, disabled?: boolean) => JSX.Element;
    onCancel: () => void;
}

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*])\S{8,}$/;

const UserPasswordForm: React.FC<UserPasswordFormProps> = ({ mode, renderButton, onCancel }) => {
    const { userDetail, setUserDetail, setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })));

    const isUpdateMode = mode === 'update';
    const passwordLabel = isUpdateMode ? "New Password" : "Password";
    const confirmPasswordLabel = isUpdateMode ? "Confirm New Password" : "Confirm Password";

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [passwordError, setPasswordError] = useState("");

    const hasSpace = (value: string) => /\s/.test(value);

    const isSaveDisabled = () => {
        if (!password.trim() || !confirmPassword.trim() || password !== confirmPassword || hasSpace(password) || hasSpace(confirmPassword) || passwordError?.length > 0) {
            return true;
        }
        return false;
    };

    const validatePassword = () => {
        if (hasSpace(password) || hasSpace(confirmPassword)) {
            setPasswordError("Password cannot contain spaces.");
            return false;
        }
        if (!PASSWORD_REGEX.test(password)) {
            setPasswordError("Password must be at least 8 characters and include uppercase, lowercase and a special character.");
            return false;
        }
        return true;
    };

    const onClickOfSave = async () => {
        if (!validatePassword()) {
            return;
        }

        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        try {
            const payload = {
                new_password: password
            }
            const res = await agent.updateUserPassword.put(payload);
            if (!res.status) {
                handleError(res);
            } else {
                showSuccess(isUpdateMode ? "Password Updated Successfully." : "Password Set Successfully.");
                setUserDetail((prev) => ({
                    ...prev,
                    flag_password: true,
                }));
                onCancel();
            }
        } finally {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    };

    const renderPasswordVisibilityAdornment = (isVisible: boolean, onToggle: () => void) => (
        <InputAdornment position="end">
            <IconButton
                aria-label={isVisible ? "Hide password" : "Show password"}
                onClick={onToggle}
                edge="end"
                size="small"
            >
                {isVisible ? (
                    <VisibilityOffIcon className={style.passwordVisibilityIcon} />
                ) : (
                    <VisibilityIcon className={style.passwordVisibilityIcon} />
                )}
            </IconButton>
        </InputAdornment>
    );

    return (
        <>
            <EmailAddressDisplay email={userDetail?.user_email} />

            <Grid item xs={12}>
                <Grid container direction="column">
                    <Grid item mb={1}>
                        <Typography className={popupStyles.popupFieldTypography}>
                            {passwordLabel} <Required />
                        </Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            type={showPassword ? "text" : "password"}
                            value={password}
                            helperText={passwordError}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\s/g, "");
                                setPassword(value);
                                if (passwordError && PASSWORD_REGEX.test(value)) {
                                    setPasswordError("");
                                }
                            }}
                            onBlur={() => validatePassword()}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    border: passwordError ? '1px solid #F04438 !important' : undefined,
                                }
                            }}
                            FormHelperTextProps={{
                                className: style.passwordHelperText,
                            }}
                            placeholder={`Enter ${passwordLabel}`}
                            className={`${contactStyle.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                            InputProps={{
                                endAdornment: renderPasswordVisibilityAdornment(showPassword, () => setShowPassword((prev) => !prev)),
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Grid container direction="column">
                    <Grid item mb={1}>
                        <Typography className={popupStyles.popupFieldTypography}>
                            {confirmPasswordLabel} <Required />
                        </Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value.replace(/\s/g, ""))}
                            placeholder={`${confirmPasswordLabel}`}
                            className={`${contactStyle.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                            InputProps={{
                                endAdornment: renderPasswordVisibilityAdornment(showConfirmPassword, () => setShowConfirmPassword((prev) => !prev)),
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} mt={2}>
                <Grid container gap="12px" direction="row" flexWrap="nowrap" justifyContent="center">
                    {renderButton("Cancel", onCancel, 'borderedButtonStyle', "130px")}
                    {renderButton("Save", onClickOfSave, 'primaryButtonStyle', "130px", isSaveDisabled())}
                </Grid>
            </Grid>
        </>
    );
};

export default UserPasswordForm;
